import { groupBySection, packColumns, type MenuBlock } from '../paginate.js';
import { featuredItem } from './cafe-panels.js';
import {
  bilingual,
  hasTitle,
  image,
  listColumns,
  listCss,
  listItem,
  sectionSubtitle,
  sectionTitle,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * A pizzeria in front of the oven: dark wood and ember glow, the featured pizza huge and round
 * at the edge of the screen in a ring of fire, and the menu beside it under flame-coloured
 * headings.
 */

const S = '.mb-t-wood-fired';

function block(ctx: RenderContext, b: MenuBlock): string {
  const head = hasTitle(b.section)
    ? `<div class="wf-head">${sectionTitle(ctx, b.section, 'wf-title')}${b.continued ? '' : sectionSubtitle(ctx, b.section, 'wf-sub')}</div>`
    : '';
  return `<div class="wf-sec">${head}${b.items.map((i) => listItem(ctx, i, 'wf')).join('')}</div>`;
}

export const woodFired: MenuTemplate = {
  id: 'wood-fired',
  name: 'Wood-fired',
  description: 'Dark wood and ember glow, a giant pizza in a ring of fire, and flame-coloured headings. Made for pizza.',
  defaultTheme: {
    backgroundColor: '#18110d',
    surfaceColor: '#241a14',
    textColor: '#f6ede4',
    primaryColor: '#ffffff',
    accentColor: '#ff7a1a',
    fontId: 'street',
    backgroundMediaId: null,
  },
  variants: [
    { id: 'ember', name: 'Ember', theme: {} },
    { id: 'basil', name: 'Basil oven', theme: { accentColor: '#7cc04b', backgroundColor: '#121611', surfaceColor: '#1b2119' } },
    { id: 'smoke', name: 'Smoke', theme: { accentColor: '#e5c79c', backgroundColor: '#1a1a1a', surfaceColor: '#242424', fontId: 'classic' } },
    { id: 'tomato', name: 'Tomato', theme: { accentColor: '#ff3b2f', backgroundColor: '#1a0f0e' } },
  ],
  cuisines: ['pizza'],
  uses: { featured: true, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    const portrait = orientation === 'portrait';
    const featured = featuredItem(board);
    return packColumns(
      groupBySection(board, featured?.id),
      listColumns(board, {
        count: 2,
        capacity: portrait ? (featured ? 62 : 98) : 50,
        heading: 4.4,
        item: { nameEm: 1.45, descEm: 0.85, perLine: portrait ? 38 : 34, gap: 1.2 },
      }),
      { balance: true },
    );
  },

  render(ctx) {
    const { board } = ctx;
    const item = featuredItem(board);
    const pizza = item ? image(ctx, item.imageMediaId, 'wf-pizza-img') : '';
    const header = [
      '<div class="wf-header">',
      bilingual(ctx, board.brand.name, board.brand.nameAr, 'wf-brand'),
      bilingual(ctx, board.settings.title ?? board.name, board.settings.titleAr, 'wf-heading'),
      bilingual(ctx, board.brand.tagline, board.brand.taglineAr, 'wf-tagline'),
      '</div>',
    ].join('');
    const lists = `<div class="wf-cols">${ctx.page.columns
      .map((c) => `<div class="wf-col">${c.map((b) => block(ctx, b)).join('')}</div>`)
      .join('')}</div>`;
    return `${pizza ? `<div class="wf-pizza">${pizza}</div>` : ''}<div class="wf-main${pizza ? '' : ' wf-full'}">${header}${lists}</div>`;
  },

  css: `
${S}{padding:0;
  background-image:radial-gradient(ellipse at 10% 95%,color-mix(in srgb,var(--mb-accent) 30%,transparent),transparent 55%),
    repeating-linear-gradient(90deg,rgba(255,255,255,.015) 0 .3em,transparent .3em 2.6em),
    linear-gradient(180deg,rgba(0,0,0,.2),transparent 30%)}
${S} .wf-pizza{position:absolute;z-index:1;left:-14em;top:50%;transform:translateY(-50%);width:62em;height:62em;border-radius:50%;
  box-shadow:0 0 0 .7em var(--mb-accent),0 0 6em 1.5em color-mix(in srgb,var(--mb-accent) 55%,transparent),0 2em 4em rgba(0,0,0,.6)}
${S} .wf-pizza-img{width:100%;height:100%;object-fit:cover;border-radius:50%;display:block}
${S}.mb-portrait .wf-pizza{left:50%;top:-20em;transform:translateX(-50%);width:56em;height:56em}
${S} .wf-main{position:relative;z-index:2;flex:1;min-height:0;display:flex;flex-direction:column;margin-left:52em;padding:3em 3.4em 2.4em 0}
${S}.mb-portrait .wf-main{margin:39em 0 0;padding:0 3.4em 2.4em}
${S} .wf-main.wf-full{margin:0;padding:3em 3.4em 2.4em}
${S} .wf-header{text-align:center;margin-bottom:1.8em}
${S} .wf-brand{font-family:var(--mb-font-script);font-size:2.6em;color:var(--mb-accent);line-height:1}
${S} .wf-brand.mb-2nd{font-size:1.8em}
${S} .wf-heading{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:5em;text-transform:uppercase;
  color:var(--mb-primary);line-height:.95;letter-spacing:.03em;text-shadow:0 0 .4em color-mix(in srgb,var(--mb-accent) 45%,transparent)}
${S} .wf-heading.mb-2nd{font-size:2.8em}
${S} .wf-tagline{font-size:1em;letter-spacing:.3em;text-transform:uppercase;opacity:.8;margin-top:.5em}
${S} .wf-cols{flex:1;min-height:0;display:grid;grid-template-columns:1fr 1fr;gap:3em}
${S} .wf-col{min-width:0;display:flex;flex-direction:column;gap:1.6em}
${S} .wf-head{margin-bottom:.9em;padding-bottom:.4em;border-bottom:.18em dashed color-mix(in srgb,var(--mb-accent) 70%,transparent)}
${S} .wf-title{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2.4em;text-transform:uppercase;
  color:var(--mb-accent);line-height:1}
${S} .wf-title.mb-2nd{font-size:1.7em}
${S} .wf-sub{font-family:var(--mb-font-script);font-size:1.2em;opacity:.85}
${listCss(S, 'wf', { name: '1.45em', price: '1.45em', desc: '.85em', leader: '.15em dotted rgba(255,255,255,.28)' })}
${S} .wf-item{margin-bottom:1em}
${S} .wf-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);text-transform:uppercase;letter-spacing:.02em}
${S} .wf-price{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);color:var(--mb-accent)}
`,
};
